import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import {
  getAnalysisDataSets,
  getOperatingPointsBhp,
  getOperatingPointsTdh,
  getProducedDataSetsTdh,
  getRequiredDataSetsBhp
} from "@/libs/charting";
import type {
  PredictiveCalcs,
  PumpTornadoChartContext,
  PumpTornadoChartData
} from ".";
import type { PrecomputedPredictions } from "./usePredictiveCalcs";
import { getStartupBhpRows } from "./bhp/getStartupBhpRows";
import { getRorDataSets } from "../_common/getRorDataSets";
import { getStartupTdhRows, usePredictiveCalcs } from ".";
import { buildTornadoPumpChartContext } from "../_common/getContext";
import type { ConvertableUnit } from "@/libs/units";
import type { DestructuredScenario } from "@/composables/useDestructureScenario";
import { getFrequencyRatio } from "@/libs/calculations/pumps/pumpData";
import { getDensityCorrectionRatio } from "../_common/densityCorrection";

// This only cares about the full stack and primary pump
// Cannot pass in individual pumps to this
export const usePumpChartingData = (
  minFrequency: number,
  maxFrequency: number,
  scenarioCalculations: ScenarioCalculations,
  targetLiquidFlowUnit: ConvertableUnit,
  targetPowerUnit: ConvertableUnit,
  targetLargeLengthUnit: ConvertableUnit,
  targetPressureUnit: ConvertableUnit,
  useEvaluationOverrides?: boolean,
  displayRealHzLine?: boolean
): PumpTornadoChartData | undefined => {
  const context = buildTornadoPumpChartContext(
    minFrequency,
    maxFrequency,
    scenarioCalculations,
    targetLiquidFlowUnit,
    targetLargeLengthUnit,
    targetPressureUnit,
    targetPowerUnit,
    useEvaluationOverrides
  );
  if (!context || !context.destructuredScenario) {
    return undefined;
  }
  return completePumpChartingData(
    context,
    scenarioCalculations,
    useEvaluationOverrides,
    displayRealHzLine
  );
};

// Everything after the chart context: per-frequency predictions, datasets and
// startup rows. Split out so the tornado chart worker can build the context
// while the per-frequency predictions run in parallel elsewhere, then pass
// them in as precomputedPredictions.
export const completePumpChartingData = (
  context: PumpTornadoChartContext,
  scenarioCalculations: ScenarioCalculations,
  useEvaluationOverrides?: boolean,
  displayRealHzLine?: boolean,
  precomputedPredictions?: PrecomputedPredictions
): PumpTornadoChartData => {
  // These are the same for both tdh and bhp
  const predictedCalcs = usePredictiveCalcs(
    context.destructuredScenario as DestructuredScenario,
    context.hypotheticalPumpFrequenciesIncludeRealHz || [],
    useEvaluationOverrides,
    precomputedPredictions
  );
  const { targetXUnit, targetTdhYUnit, targetBhpYUnit } = context;

  // For each predicted calculation, we need to get a density correction ratio
  // so the predicted points align with the produced TDH curve which is anchored
  // in real data and thus implicitly includes density effects.
  // Without this, the predicted points could visually drift away from the produced
  // curve and be misaligned with the ROR lines which share the same density-corrected
  // anchor. For now we are not using the density correction ratio found with each hz
  // set and instead are just using the correction ratio at the real pump frequency and
  // reserviing these in case they are needed in the future.
  const predictedCalcsWithDensity = Object.entries(predictedCalcs.value).reduce(
    (acc, [frequency, predictedCalc]) => {
      const { hypotheticalCalcs } = predictedCalc;
      const primaryPump = hypotheticalCalcs.primaryPump.value;
      // Anchor this to the discharge flow rate since we use this to adjust
      // the y values on the head chart and it uses the discharge rate
      const hypotheticalPumpDischargeFlowRateBBLD = primaryPump
        ? hypotheticalCalcs.pumpCalculations.individual.value[primaryPump.id]
            ?.dischargeStageCalculations?.dischargeFluidPropertiesContext
            .flowRatesInSitu?.fluidFlowRateBBLD ?? 0
        : 0;
      const hypotheticalFrequencyRatio = getFrequencyRatio(
        hypotheticalCalcs.pumpCalculations.aggregate.operatingRPM.value,
        hypotheticalCalcs.isScenarioEspShore.value
      );

      const hypotheticalRequiredTargetTDH =
        scenarioCalculations.pumpCalculations.aggregate
          .requiredTotalDynamicHeadFt.value;
      const hypotheticalRequiredTargetPressurePSIA =
        scenarioCalculations.pumpCalculations.aggregate.differentialPressurePSIA
          .value;

      const predictedDensityCorrectionRatios = getDensityCorrectionRatio(
        hypotheticalPumpDischargeFlowRateBBLD,
        hypotheticalCalcs.ascendingOrderPumps.value,
        hypotheticalCalcs.pumpCalculations.aggregate.gasHeadRerateRatio.value,
        context.pumpWearHeadCorrectionRatio,
        context.pumpWearBHPCorrectionRatio,
        hypotheticalCalcs.dischargeFluidDensityLBMFT3.value,
        hypotheticalFrequencyRatio,
        hypotheticalRequiredTargetTDH,
        hypotheticalRequiredTargetPressurePSIA,
        hypotheticalCalcs.pumpCalculations.aggregate.requiredPumpBrakeHP.value
      );

      return {
        ...acc,
        [frequency]: {
          ...predictedCalc,
          densityCorrectionRatios: predictedDensityCorrectionRatios
        }
      };
    },
    {} as PredictiveCalcs
  );

  // Produced datasets and TDH operating points should use the density
  // correction ratio that matches the charted basis (TDH for ESP, pressure for HPS).
  // These are the correction ratio using the real pump frequency
  const realDensityCorrectionRatios =
    predictedCalcsWithDensity[context.pumpHz]?.densityCorrectionRatios;
  const realProducedDataSetDensityCorrectionRatio = context.isScenarioEspShore
    ? realDensityCorrectionRatios?.densityCorrectionTDHRatio || 1
    : realDensityCorrectionRatios?.densityCorrectionPressureRatio || 1;

  // Frequency line data sets:
  // this includes a real-Hz dataset used as an anchor for derived points
  // (for example estimated required TDH), but we intentionally do not draw
  // that real-Hz line in the tornado chart so only comparison frequencies show.
  const allProducedDataSetsTdh = getProducedDataSetsTdh(
    context,
    predictedCalcsWithDensity,
    false
  );

  // ROR Datasets for each chart
  const rorDataSetsTdh = getRorDataSets(
    context,
    "tdh",
    predictedCalcsWithDensity
  );
  const rorDataSetsBhp = getRorDataSets(context, "bhp");

  // BHP Datasets
  // getRequiredDataSetsBhp will contain our estimated required bhp point for real frequency
  // along with the real frequency dataset that needs removed so it's not charted
  const allRequiredDataSetsBhp = getRequiredDataSetsBhp(context);
  const realHzBhpDataSetIndex = allRequiredDataSetsBhp.findIndex(
    ds => ds.originalPumpHzFrequency === context.pumpHz
  );

  // TDH Operating points
  const operatingPointsTdh = getOperatingPointsTdh(
    context,
    realProducedDataSetDensityCorrectionRatio
  );
  // BHP Operating points
  const operatingPointsBhp = getOperatingPointsBhp(context);

  // BHP/TDH Analysis Datasets
  // We pass in discharge OP from TDH operating points and
  // we pass in the intake OP from BHP operating points
  const analysisDataSets = getAnalysisDataSets(
    context,
    predictedCalcsWithDensity,
    operatingPointsTdh.operatingPoints[0],
    operatingPointsBhp.operatingPoints[0],
    context.destructuredScenario as DestructuredScenario
  );

  // BHP Startup rows
  const bhpStartupRows = getStartupBhpRows(
    context,
    predictedCalcsWithDensity,
    context.destructuredScenario as DestructuredScenario
  );
  // TDH Startup rows
  const tdhStartupRows = getStartupTdhRows(
    context,
    predictedCalcsWithDensity,
    context.destructuredScenario as DestructuredScenario
  );

  // If we have to reduce the rate for any pump at any frequency for the bhp chart,
  // then we need to let the chart know since we'll show a message
  const bhpRateReductionsByFrequency = allRequiredDataSetsBhp.map(dataSet => {
    const { adjustedPumpHzFrequency, producedBhpRateCorrectedPumps } = dataSet;
    return {
      frequency: adjustedPumpHzFrequency,
      usesBhpRateReduction: producedBhpRateCorrectedPumps.length > 0,
      producedBhpRateCorrectedPumps: producedBhpRateCorrectedPumps.map(p => {
        return `${p.r_pump?.series_model}`;
      })
    };
  });
  const usesBhpRateReduction = !!bhpRateReductionsByFrequency.find(
    r => r.usesBhpRateReduction
  );

  // Hide real hz line from charts
  const realHzTdhDataSetIndex = allProducedDataSetsTdh.findIndex(
    ds => ds.originalPumpHzFrequency === context.pumpHz
  );

  const renderedProducedDataSetsTdh =
    !displayRealHzLine && realHzTdhDataSetIndex !== -1
      ? allProducedDataSetsTdh.filter(
          (_, index) => index !== realHzTdhDataSetIndex
        )
      : allProducedDataSetsTdh;
  const renderedRequiredDataSetsBhp =
    !displayRealHzLine && realHzBhpDataSetIndex !== -1
      ? allRequiredDataSetsBhp.filter(
          (_, index) => index !== realHzBhpDataSetIndex
        )
      : allRequiredDataSetsBhp;
  const operatingDataSetsTdh = renderedProducedDataSetsTdh.map(
    ds => ds.dataSet
  );
  const operatingDataSetsBhp = renderedRequiredDataSetsBhp.map(
    ds => ds.dataSet
  );

  return {
    context,
    tdh: {
      operatingPoints: operatingPointsTdh.operatingPoints,
      analysisDataSet: analysisDataSets.tdh,
      rorDataSets: rorDataSetsTdh,
      operatingDataSets: operatingDataSetsTdh,
      targetXUnit,
      targetYUnit: targetTdhYUnit
    },
    bhp: {
      operatingPoints: operatingPointsBhp.operatingPoints,
      analysisDataSet: analysisDataSets.bhp,
      rorDataSets: rorDataSetsBhp,
      operatingDataSets: operatingDataSetsBhp,
      targetXUnit,
      targetYUnit: targetBhpYUnit
    },
    bhpStartupRows,
    tdhStartupRows,
    usesBhpRateReduction,
    bhpRateReductionsByFrequency
    // additionalData: {
    //   ...additionalData
    // }
  };
};
